import { Edit3, PencilLine, X } from "@tamagui/lucide-icons";
import ActionMenu, { DefaultActionMenuRender } from "@yoopta/action-menu-list";
import Divider from "@yoopta/divider";
import YooptaEditor, {
  createYooptaEditor,
  type SlateElement,
  type YooptaContentValue,
  type YooptaEditorProps,
  type YooptaPlugin,
} from "@yoopta/editor";
import Embed from "@yoopta/embed";
import { HeadingOne, HeadingThree, HeadingTwo } from "@yoopta/headings";
import Image from "@yoopta/image";
import Link from "@yoopta/link";
import LinkTool, { DefaultLinkToolRender } from "@yoopta/link-tool";
import { BulletedList, NumberedList, TodoList } from "@yoopta/lists";
import {
  Bold,
  CodeMark,
  Highlight,
  Italic,
  Strike,
  Underline,
} from "@yoopta/marks";
import Paragraph from "@yoopta/paragraph";
import Toolbar, { DefaultToolbarRender } from "@yoopta/toolbar";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import removeMarkdown from "markdown-to-text";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { OpaqueColorValue } from "react-native";
import {
  type GetThemeValueForKey,
  Text,
  type TextProps,
  View,
  XStack,
} from "tamagui";
import { storage } from "../core/firebaseInit";
import { trackEvent } from "../core/trackEvent";
import { usePerformance } from "../core/usePerformance";
import { getImageInfo } from "../core/utils";
import { useLockScroll } from "../helper/useScrollLock";
import { sanitizeForFirestore } from "../modules/core/sanitizeForFirestore";
import { Body } from "./Body";
import { Button } from "./Button";
import { log } from "./log";
import { Title } from "./Title";

export type EditorValue = { data?: YooptaContentValue; markdown?: string };

const MARKS = [Bold, Italic, CodeMark, Underline, Strike, Highlight];
const TOOLS = {
  Toolbar: {
    tool: Toolbar,
    render: DefaultToolbarRender,
  },
  ActionMenu: {
    tool: ActionMenu,
    render: DefaultActionMenuRender,
  },
  LinkTool: {
    tool: LinkTool,
    render: DefaultLinkToolRender,
  },
};

const plugins = [
  Paragraph,
  Divider,
  Link,
  NumberedList,
  BulletedList,
  TodoList,
  HeadingOne,
  HeadingTwo,
  HeadingThree,
  Embed.extend({
    options: { maxSizes: { maxHeight: 300, maxWidth: 600 } },
  }),
];

export const Editor: React.FC<
  {
    onChange?: (value: EditorValue) => void;
    value?: EditorValue;
    id: string;
    getStoragePathSuffix?: () => string;
  } & Omit<YooptaEditorProps, "editor" | "plugins" | "value">
> = ({
  onChange,
  value,
  readOnly,
  id,
  getStoragePathSuffix,
  ...yooptaProps
}) => {
  const editor = useMemo(() => createYooptaEditor(), []);
  const selectionRef = useRef(null);
  const performanceMeasure = usePerformance("UPLOAD_DOC_IMAGE");

  const _onChange = useCallback(
    (data: YooptaContentValue) => {
      onChange?.({ data, markdown: editor.getMarkdown(data) });
    },
    [onChange, editor],
  );

  const isEmpty = !value || !Object.keys(value).length;

  useEffect(() => {
    if (!readOnly && isEmpty) {
      editor.insertBlock("Paragraph", { at: 0, focus: true });
    }
  }, [editor, readOnly, isEmpty]);

  const uploadFile = React.useCallback(
    async (uploadFileParam?: File) => {
      const storagePathSuffix = await getStoragePathSuffix?.();

      const uploadFile = uploadFileParam;

      if (!storagePathSuffix) {
        log.dev("Error - no storagePathSuffix set");
        throw Error();
      }

      if (!uploadFile) {
        log.dev("Error - no file selected");
        throw Error();
      }

      performanceMeasure.start();

      let url = "";

      try {
        // Create a reference to 'images/mountains.jpg'
        const storageRef = ref(storage, `doc_media/${storagePathSuffix}`);

        const snapshot = await uploadBytes(storageRef, uploadFile);
        log.dev("Uploaded file!", snapshot);

        url = await getDownloadURL(snapshot.ref); // include token for cache busts.split("&")[0];

        log.dev("got url..", url);
        log.dev("sizes", getImageInfo(url));

        performanceMeasure.stop();

        trackEvent("doc_media_uploaded");
      } catch (e) {
        console.error("Got invalid select file", e);
        trackEvent("doc_media_upload_error");
      }

      return { src: url };
    },
    [getStoragePathSuffix, performanceMeasure],
  );

  const activePlugins = useMemo(() => {
    const p: YooptaPlugin<
      Record<string, SlateElement>,
      Record<string, unknown>
    >[] = [...plugins];

    if (getStoragePathSuffix) {
      p.push(
        Image.extend({
          options: {
            maxSizes: { maxHeight: 300, maxWidth: 600 },
            onUpload: uploadFile,
          },
        }),
      );
    }

    return p;
  }, [getStoragePathSuffix, uploadFile]);

  return (
    <>
      <style>{`
      /* HACK TO MAKE THE VIDEO EMBED WORK BETTER */
      .yoopta-embed {max-width: 100% !important; max-height: 300px !important;}
      .yoopta-embed iframe {max-width: 100% !important; max-height: 300px !important;}
      .yoopta-embed .yoo-embed-flex {max-width: 100% !important; max-height: 300px !important;}
      .yoopta-embed .yoo-embed-relative {width: 100% !important; max-height: 300px !important;}
      `}</style>
      <View
        marginLeft={readOnly ? 0 : 24}
        ref={selectionRef}
        position="relative"
        height="100%"
        onPress={(e) => {
          if (e.target === e.currentTarget) {
            const blocks = Object.values(editor.getEditorValue()).sort(
              (a, b) => a.meta.order - b.meta.order,
            );

            const lastBlockId = blocks[blocks.length - 1].id;

            if (lastBlockId) {
              editor.focusBlock(lastBlockId);
            } else {
              editor.focus();
            }
          }
        }}
      >
        <View onPress={(e) => e.preventDefault()}>
          <YooptaEditor
            id={id}
            editor={editor}
            plugins={activePlugins}
            value={value?.data}
            width={"100%"}
            marks={MARKS}
            tools={TOOLS}
            selectionBoxRoot={selectionRef}
            onChange={_onChange}
            style={{ paddingBottom: readOnly ? 0 : 40 }}
            readOnly={readOnly}
            {...yooptaProps}
          />
        </View>
      </View>
    </>
  );
};

type EditorFormProps = {
  id: string;
  value?: EditorValue | null;
  onSave: (value: EditorValue | null) => void;
  canEdit?: boolean;
  getStoragePathSuffix?: () => string;
};

export const RevealEditorArea = observer(
  ({ onPress }: { onPress: () => void }) => {
    return (
      <View
        cursor="pointer"
        onPress={onPress}
        borderWidth={1}
        borderColor={"$divider"}
        borderStyle="dashed"
        padding="$medium"
        alignItems="center"
        minHeight={80}
        justifyContent="center"
      >
        <XStack gap="$medium">
          <PencilLine color={"$muted"} />
          <Body cursor="pointer" fontStyle="italic" muted>
            Tap to add description
          </Body>
        </XStack>
      </View>
    );
  },
);

export const EditorForm: React.FC<EditorFormProps> = observer((props) => {
  const [value, setValue] = useState<EditorValue | null>(
    () => props.value ?? null,
  );
  const [showEditorModal, setShowEditorModal] = useState(false);
  const canEdit = props.canEdit ?? true;
  const renderContainerRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );

  useLockScroll(showEditorModal);

  const isEmpty = !value || !Object.keys(value).length;

  const showEditor = useCallback(() => {
    setContentHeight(renderContainerRef.current?.offsetHeight);
    // Delay the modal a bit so we have time to set the height on the div and avoid the scroll jumping
    setTimeout(() => {
      setShowEditorModal(true);
    }, 20);
  }, []);

  const hideEditor = useCallback(() => {
    setShowEditorModal(false);
    setContentHeight(undefined);
  }, []);

  if (canEdit && isEmpty && !showEditorModal) {
    return <RevealEditorArea onPress={showEditor} />;
  }

  return (
    <>
      <View ref={renderContainerRef} height={contentHeight}>
        {!showEditorModal && (
          <Editor
            id={`${props.id}-render`}
            readOnly
            value={toJS(value ?? undefined)}
            getStoragePathSuffix={props.getStoragePathSuffix}
          />
        )}
      </View>
      {canEdit && (
        <XStack
          onPress={showEditor}
          cursor="pointer"
          alignItems="center"
          gap="$small"
        >
          <Edit3 />
          <Body>Edit</Body>
        </XStack>
      )}
      {canEdit && showEditorModal && (
        <View
          zIndex={1000}
          style={{
            position: "fixed",
            overflowX: "hidden",
            overflowY: "auto",
            height: "100dvh",
          }}
          top={0}
          left={0}
          right={0}
          alignItems="center"
          background={"rgba(0,0,0,0.8)"}
        >
          <View
            maxWidth={600}
            width="100%"
            padding={"$size.medium"}
            background={"#fff"}
            height="100%"
          >
            <XStack alignItems="center" justifyContent="space-between">
              <Title>Description</Title>
              <View cursor="pointer" onPress={hideEditor}>
                <X />
              </View>
            </XStack>
            <View flex={1}>
              <Editor
                id={`${props.id}`}
                value={toJS(value ?? undefined)}
                onChange={(data) => {
                  setValue(data);
                }}
                autoFocus
                getStoragePathSuffix={props.getStoragePathSuffix}
              />
            </View>
          </View>
          <Button
            style={{ position: "fixed" }}
            bottom={0}
            width="100%"
            maxWidth={600}
            type="primary"
            onPress={() => {
              props.onSave(sanitizeForFirestore(value));
              hideEditor();
            }}
          >
            Save
          </Button>
        </View>
      )}
    </>
  );
});

export const EditorRender: React.FC<{
  value?: EditorValue | null;
  id?: string;
  color?: OpaqueColorValue | GetThemeValueForKey<"color">;
  emptyText?: string;
  maxHeight?: number;
}> = ({ value, id, color, emptyText }) => {
  const editor = useMemo(() => createYooptaEditor(), []);

  if (!value || !Object.keys(value).length) {
    return emptyText ? (
      <Body muted fontStyle="italic">
        {emptyText}
      </Body>
    ) : undefined;
  }

  return (
    <View>
      <Text color={color}>
        <YooptaEditor
          id={id}
          editor={editor}
          plugins={plugins}
          value={value.data ?? undefined}
          width={"100%"}
          readOnly
          style={{
            marginBottom: 0,
            paddingBottom: 0,
            minHeight: "auto",
          }}
        />
      </Text>
    </View>
  );
};

export const EditorRenderInline: React.FC<
  {
    value?: EditorValue | null;
    emptyText?: string;
  } & TextProps
> = ({ value, emptyText, ...textProps }) => {
  if (!value?.markdown) {
    return emptyText ? (
      <Body muted fontStyle="italic">
        {emptyText}
      </Body>
    ) : undefined;
  }

  return (
    <View>
      <Text {...textProps}>{removeMarkdown(value.markdown)}</Text>
    </View>
  );
};
